import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

export default function QuickSpotForm({ initialData, photographers, onSave, onCancel, courseTimings = { modality_timings: [] }, eventType = 'general', customModalities = [] }) {
  const [formData, setFormData] = useState({
    name: '',
    lat: initialData.lat,
    lng: initialData.lng,
    address: '',
    description: '',
    coverage_role: 'Wide',
    modalities: [],
    required_gear: [],
    has_flash: false,
    arrive_time: '',
    leave_time: '',
    first_athlete_time: '',
    last_athlete_time: '',
    cutoff_time: '',
    notes: '',
    max_photographers: 1,
    priority: 'medium',
    assigned_photographers: []
  });

  const [gearInput, setGearInput] = useState('');
  const [selectedPhotographers, setSelectedPhotographers] = useState([]);
  const [customModality, setCustomModality] = useState('');

  const coverageRoles = ['Wide', 'Tele', 'Crowd', 'Drone', 'Social', 'Video'];
  const priorities = ['high', 'medium', 'low'];
  
  // Show only modalities for this event type
  const allPresets = {
    sports: { 'Sports': ['Run', 'Swimming', 'Bike', 'Rowing', 'Finish Line', 'Start Line', 'Transition Zone', 'Aid Station'] },
    fitness: { 'Fitness/Hyrox': ['SKIERG', 'SLED PUSH', 'SLED PULL', 'BURPEE BROAD JUMPS', 'ROWING', 'FARMERS CARRY', 'LUNGES', 'WALL BALLS'] },
    events: { 'Events': ['Ceremony', 'Reception', 'Stage', 'Backstage', 'VIP Area', 'Entrance', 'Red Carpet'] },
    wedding: { 'Wedding': ['Ceremony', 'First Dance', 'Reception', 'Getting Ready', 'Portraits', 'Group Photos', 'Details', 'Cake Cutting'] },
    real_estate: { 'Real Estate': ['Exterior', 'Interior', 'Kitchen', 'Living Room', 'Bedroom', 'Bathroom', 'Aerial/Drone', 'Twilight'] },
    corporate: { 'Corporate': ['Headshots', 'Product', 'Office', 'Meeting Room', 'Event Space', 'Branding'] },
    general: { 'Common': ['Wide Shot', 'Detail', 'Portrait', 'Group', 'Action', 'Candid', 'Establishing Shot'] },
  };
  const modalityGroups = (() => {
    const groups = allPresets[eventType] || allPresets.general;
    if (customModalities.length > 0) return { ...groups, 'Custom': customModalities };
    return groups;
  })();

  // Common gear suggestions by scene type
  const commonGearBySport = {
    'Run': ['70-200mm f/2.8', '24-70mm f/2.8', 'Wide angle 16-35mm', 'Telephoto 300mm+'],
    'Swimming': ['70-200mm f/2.8', 'Telephoto 300mm+', 'Underwater housing', 'Waterproof camera'],
    'Bike': ['70-200mm f/2.8', 'Telephoto 300mm+', '24-70mm f/2.8', 'Drone'],
    'Ceremony': ['24-70mm f/2.8', '70-200mm f/2.8', '35mm f/1.4', 'Flash/Strobe'],
    'Reception': ['24-70mm f/2.8', '35mm f/1.4', 'Flash/Strobe', 'Wide angle 16-35mm'],
    'Portraits': ['85mm f/1.4', '70-200mm f/2.8', '35mm f/1.4', 'Reflector'],
    'Getting Ready': ['35mm f/1.4', '24-70mm f/2.8', 'Flash/Strobe'],
    'Exterior': ['Wide angle 16-35mm', '24-70mm f/2.8', 'Drone', 'Tripod'],
    'Interior': ['Wide angle 16-35mm', 'Tripod', 'Flash/Strobe', 'Light stands'],
    'Twilight': ['Wide angle 16-35mm', 'Tripod', 'Flash/Strobe', 'Timer remote'],
    'Aerial/Drone': ['Drone', 'Spare batteries', 'ND filters'],
    'Headshots': ['85mm f/1.4', '70-200mm f/2.8', 'Flash/Strobe', 'Backdrop'],
    'Product': ['Macro lens', '24-70mm f/2.8', 'Light box', 'Tripod'],
    'Stage': ['70-200mm f/2.8', 'Telephoto 300mm+', 'Monopod'],
    'General': ['24-70mm f/2.8', '70-200mm f/2.8', 'Wide angle 16-35mm', 'Flash/Strobe', 'Drone']
  };

  // Get suggested gear based on selected modalities
  const getSuggestedGear = () => {
    const allSuggestions = new Set();
    formData.modalities.forEach(modality => {
      const gearList = commonGearBySport[modality] || commonGearBySport['General'];
      gearList.forEach(gear => allSuggestions.add(gear));
    });
    return Array.from(allSuggestions);
  };
  // Get timing suggestions based on selected modalities
  const getTimingSuggestions = () => {
    const suggestions = [];
    
    // Handle new structure: courseTimings is single object with modality_timings array
    if (courseTimings && courseTimings.modality_timings) {
      courseTimings.modality_timings.forEach(modalityTiming => {
        if (formData.modalities.includes(modalityTiming.modality)) {
          // Add suggestion for this modality with available times
          const timeParts = [];
          if (modalityTiming.start_time) timeParts.push(`Start: ${modalityTiming.start_time}`);
          if (modalityTiming.first_finisher_time) timeParts.push(`First Finisher: ${modalityTiming.first_finisher_time}`);
          if (modalityTiming.last_finisher_time) timeParts.push(`Last Finisher: ${modalityTiming.last_finisher_time}`);
          if (modalityTiming.cutoff_time) timeParts.push(`Cutoff: ${modalityTiming.cutoff_time}`);
          
          if (timeParts.length > 0) {
            suggestions.push({
              modality: modalityTiming.modality,
              timeInfo: timeParts.join(' | '),
              data: modalityTiming
            });
          }
        }
      });
    }
    
    return suggestions;
  };

  const toggleModality = (modality) => {
    setFormData(prev => {
      const newModalities = prev.modalities.includes(modality)
        ? prev.modalities.filter(m => m !== modality)
        : [...prev.modalities, modality];
      
      // Auto-set name to first selected modality if name is empty or matches old modality
      let newName = prev.name;
      if (newModalities.length > 0 && (!prev.name || prev.modalities.includes(prev.name))) {
        newName = newModalities[0];
      } else if (newModalities.length === 0) {
        newName = '';
      }
      
      // Auto-populate arrive/leave times when adding a new modality
      let arriveTime = prev.arrive_time;
      let leaveTime = prev.leave_time;
      
      if (newModalities.includes(modality) && !prev.modalities.includes(modality)) {
        if (courseTimings && courseTimings.modality_timings) {
          const modalityTiming = courseTimings.modality_timings.find(mt => mt.modality === modality);
          if (modalityTiming) {
            // Arrive time = 1 hour before start_time
            if (modalityTiming.start_time) {
              const [hours, minutes] = modalityTiming.start_time.split(':').map(Number);
              const newHours = hours - 1;
              if (newHours >= 0) {
                arriveTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
              }
            }
            // Leave time = cutoff_time
            if (modalityTiming.cutoff_time) {
              leaveTime = modalityTiming.cutoff_time;
            }
          }
        }
      }
      
      return {
        ...prev,
        modalities: newModalities,
        name: newName,
        arrive_time: arriveTime,
        leave_time: leaveTime
      };
    });
  };

  const addCustomModality = () => {
    if (customModality.trim() && !formData.modalities.includes(customModality.trim())) {
      setFormData(prev => {
        const newModalities = [...prev.modalities, customModality.trim()];
        // Auto-set name if empty
        const newName = !prev.name ? customModality.trim() : prev.name;
        return {
          ...prev,
          modalities: newModalities,
          name: newName
        };
      });
      setCustomModality('');
    }
  };

  const removeModality = (modality) => {
    setFormData(prev => ({
      ...prev,
      modalities: prev.modalities.filter(m => m !== modality)
    }));
  };

  const handleAddGear = () => {
    if (gearInput.trim()) {
      setFormData(prev => ({
        ...prev,
        required_gear: [...prev.required_gear, gearInput.trim()]
      }));
      setGearInput('');
    }
  };

  const handleRemoveGear = (index) => {
    setFormData(prev => ({
      ...prev,
      required_gear: prev.required_gear.filter((_, i) => i !== index)
    }));
  };

  const togglePhotographer = (photographerId) => {
    setSelectedPhotographers(prev => {
      if (prev.includes(photographerId)) {
        return prev.filter(id => id !== photographerId);
      } else if (prev.length < formData.max_photographers) {
        return [...prev, photographerId];
      } else {
        return prev;
      }
    });
  };

  const handleSubmit = () => {
    if (!formData.name) {
      alert('Please enter a spot name');
      return;
    }
    
    onSave({ ...formData, assigned_photographers: selectedPhotographers });
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
        <p className="font-medium text-emerald-900">📍 Location: {initialData.lat.toFixed(5)}, {initialData.lng.toFixed(5)}</p>
      </div>

      <div>
        <Label>Spot Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="e.g., Station 1 - SKIERG (auto-fills from activity)"
        />
        <p className="text-xs text-gray-500 mt-1">Auto-fills from selected activity, but you can edit it</p>
      </div>

      <div>
        <Label>Address/Location</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="e.g., Main Arena Floor, Section A"
        />
        <p className="text-xs text-gray-500 mt-1">Enter location description or floor plan reference</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Coverage Role</Label>
          <Select
            value={formData.coverage_role}
            onValueChange={(value) => setFormData({ ...formData, coverage_role: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {coverageRoles.map(role => (
                <SelectItem key={role} value={role}>{role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Priority</Label>
          <Select
            value={formData.priority}
            onValueChange={(value) => setFormData({ ...formData, priority: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {priorities.map(priority => (
                <SelectItem key={priority} value={priority}>
                  {priority.charAt(0).toUpperCase() + priority.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Description</Label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Brief description"
        />
      </div>

      <div>
        <Label>Activity Stations/Modalities (Select all that apply)</Label>
        <div className="mt-2 space-y-3">
          {Object.entries(modalityGroups).map(([group, modalities]) => (
            modalities.length > 0 && (
              <div key={group}>
                <p className="text-xs font-semibold text-gray-600 mb-2">{group}</p>
                <div className="flex flex-wrap gap-2">
                  {modalities.map(modality => (
                    <button
                      key={modality}
                      type="button"
                      onClick={() => toggleModality(modality)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                        formData.modalities.includes(modality)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {modality}
                    </button>
                  ))}
                </div>
              </div>
            )
          ))}
          
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Custom Activity</p>
            <div className="flex gap-2">
              <Input
                value={customModality}
                onChange={(e) => setCustomModality(e.target.value)}
                placeholder="Add custom station/activity"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomModality())}
              />
              <Button type="button" onClick={addCustomModality} size="sm" variant="outline">
                + Add
              </Button>
            </div>
          </div>

          {formData.modalities.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <span className="text-sm text-gray-600">Selected:</span>
              {formData.modalities.map((modality, idx) => (
                <span key={idx} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-sm flex items-center gap-1">
                  {modality}
                  <button onClick={() => removeModality(modality)} className="text-emerald-900 hover:text-red-600">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label>Required Gear</Label>
        
        {/* Suggested gear based on modality */}
        {formData.modalities.length > 0 && getSuggestedGear().length > 0 && (
          <div className="mb-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-purple-900 mb-2">💡 Suggested Gear for {formData.modalities.join(', ')}</p>
            <div className="flex flex-wrap gap-2">
              {getSuggestedGear().map((gear, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (!formData.required_gear.includes(gear)) {
                      setFormData(prev => ({ ...prev, required_gear: [...prev.required_gear, gear] }));
                    }
                  }}
                  className={`text-xs px-2 py-1 rounded transition ${
                    formData.required_gear.includes(gear)
                      ? 'bg-emerald-500 text-white cursor-default'
                      : 'bg-white border border-purple-300 text-purple-700 hover:bg-purple-100'
                  }`}
                  disabled={formData.required_gear.includes(gear)}
                >
                  {gear} {formData.required_gear.includes(gear) && '✓'}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex gap-2 mb-2">
          <Input
            value={gearInput}
            onChange={(e) => setGearInput(e.target.value)}
            placeholder="e.g., Telephoto Lens"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGear())}
          />
          <Button type="button" onClick={handleAddGear} size="sm">Add</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.required_gear.map((gear, idx) => (
            <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-sm flex items-center gap-1">
              {gear}
              <button onClick={() => handleRemoveGear(idx)} className="text-red-500">×</button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          checked={formData.has_flash}
          onCheckedChange={(checked) => setFormData({ ...formData, has_flash: checked })}
        />
        <Label>Flash Required</Label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Max Photographers</Label>
          <Input
            type="number"
            min="1"
            value={formData.max_photographers}
            onChange={(e) => setFormData({ ...formData, max_photographers: parseInt(e.target.value) || 1 })}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Arrive Time</Label>
          <Input
            type="time"
            value={formData.arrive_time}
            onChange={(e) => setFormData({ ...formData, arrive_time: e.target.value })}
          />
        </div>
        <div>
          <Label>Leave Time</Label>
          <Input
            type="time"
            value={formData.leave_time}
            onChange={(e) => setFormData({ ...formData, leave_time: e.target.value })}
          />
        </div>
      </div>

      {/* Timing suggestions based on modality */}
      {formData.modalities.length > 0 && getTimingSuggestions().length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-900 mb-2">⏰ Timing Suggestions for {formData.modalities.join(', ')}</p>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {getTimingSuggestions().map((suggestion, idx) => (
              <div
                key={idx}
                className="w-full text-left text-xs bg-white border border-blue-200 rounded px-2 py-1.5"
              >
                <div className="font-medium text-blue-700 mb-1">{suggestion.modality}</div>
                <div className="text-blue-900 text-xs">{suggestion.timeInfo}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">Use this timing info to plan photographer arrival/departure</p>
        </div>
      )}

      <div>
        <Label>Assign Photographers (Optional - {selectedPhotographers.length}/{formData.max_photographers})</Label>
        {!photographers || photographers.length === 0 ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
            No photographers assigned to this event yet. Use "Assign Photographers" at the top of the event first, then come back here.
          </p>
        ) : (
          <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
            {photographers.map(photographer => (
              <div
                key={photographer.id}
                className={`p-2 rounded cursor-pointer transition ${
                  selectedPhotographers.includes(photographer.id)
                    ? 'bg-emerald-100 border-2 border-emerald-500'
                    : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'
                }`}
                onClick={() => togglePhotographer(photographer.id)}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{photographer.name}</span>
                  {selectedPhotographers.includes(photographer.id) && (
                    <span className="text-emerald-600">✓</span>
                  )}
                </div>
                {photographer.personal_gear && photographer.personal_gear.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {photographer.personal_gear.slice(0, 2).map(g => g.name).join(', ')}
                    {photographer.personal_gear.length > 2 && ` +${photographer.personal_gear.length - 2}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label>Notes</Label>
        <Input
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Additional notes"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="btn-gradient">
          Create Spot & Assign
        </Button>
      </div>
    </div>
  );
}
